import { Button, Input } from '@fsd/ui';
import { CloseCircle, Diskette } from '@solar-icons/react';
import { Controller } from 'react-hook-form';
import type { ProductFormData } from '../../types/product';
import { FeaturesList } from './featuresList';
import { getErrorMessage, getLocalizedError } from './helpers';
import { ImageList } from './imageList';
import { LanguageSwitcher } from './languageSwitcher';
import { LocalizedInput } from './localizedInput';
import { LocalizedTextarea } from './localizedTextarea';
import { SpecificationsList } from './specificationsList';
import { StarRating } from './starRating';
import { useProductForm } from './useProductForm';

interface ProductFormProps {
  initialData?: Partial<ProductFormData>;
  onSubmit: (data: ProductFormData) => void | Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  lockedCategory?: boolean;
}

export function ProductForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
  lockedCategory = false,
}: ProductFormProps) {
  const {
    control,
    handleSubmit,
    errors,
    activeLang,
    setActiveLang,
    specifications,
    features,
    setSpecifications,
    setFeatures,
    handleFormSubmit,
    ensureLocalizedValue,
  } = useProductForm({ initialData, onSubmit });

  return (
    <div className="space-y-6">
      {/* Global Language Switcher */}
      <LanguageSwitcher activeLang={activeLang} onChange={setActiveLang} />

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
        <div className="bg-card p-6 rounded-lg border border-border space-y-6">
          <h2 className="text-lg font-semibold text-foreground">Informações Básicas</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Categoria */}
            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground block">
                Categoria <span className="text-destructive">*</span>
              </span>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    disabled={lockedCategory}
                    className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${lockedCategory ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <option value="new-drones">Drones Novos</option>
                    <option value="used-drones">Drones Usados</option>
                    <option value="buffalo">Linha Buffalo</option>
                    <option value="power-citros">Power Citros</option>
                    <option value="parts">Peças e Acessórios</option>
                  </select>
                )}
              />
              {lockedCategory && (
                <p className="text-xs text-muted-foreground">
                  Categoria definida pela página atual
                </p>
              )}
              {errors.category && (
                <p className="text-sm text-destructive">{getErrorMessage(errors.category)}</p>
              )}
            </div>

            {/* Preço */}
            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground block">
                Preço (R$) <span className="text-muted-foreground text-xs">(Opcional)</span>
              </span>
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    step="0.01"
                    placeholder="Deixe vazio se o preço não estiver disponível"
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === '' ? 0 : Number.parseFloat(value));
                    }}
                  />
                )}
              />
              <p className="text-xs text-muted-foreground">
                Se não houver preço, deixe vazio. O cliente pode consultar via WhatsApp.
              </p>
              {errors.price && (
                <p className="text-sm text-destructive">{getErrorMessage(errors.price)}</p>
              )}
            </div>
          </div>

          {/* Nome */}
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <LocalizedInput
                label="Nome do Produto"
                value={ensureLocalizedValue(field.value)}
                onChange={field.onChange}
                lang={activeLang}
                error={getLocalizedError(errors.name)}
                required
                placeholder={{
                  'pt-BR': 'Ex: XAG P100 Pro',
                  'en-US': 'Ex: XAG P100 Pro',
                  'es-ES': 'Ex: XAG P100 Pro',
                }}
              />
            )}
          />

          {/* Subtítulo */}
          <Controller
            name="subtitle"
            control={control}
            render={({ field }) => (
              <LocalizedInput
                label="Subtítulo (Opcional)"
                value={ensureLocalizedValue(field.value)}
                onChange={field.onChange}
                lang={activeLang}
                placeholder={{
                  'pt-BR': 'Ex: Drone Agrícola Profissional',
                  'en-US': 'Ex: Professional Agricultural Drone',
                  'es-ES': 'Ex: Dron Agrícola Profesional',
                }}
              />
            )}
          />

          {/* Descrição */}
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <LocalizedTextarea
                label="Descrição"
                value={ensureLocalizedValue(field.value)}
                onChange={field.onChange}
                lang={activeLang}
                error={getLocalizedError(errors.description)}
                required
                rows={6}
                placeholder={{
                  'pt-BR': 'Descreva o produto em detalhes...',
                  'en-US': 'Describe the product in detail...',
                  'es-ES': 'Describe el producto en detalle...',
                }}
              />
            )}
          />
        </div>

        {/* Imagens */}
        <section className="bg-card p-6 rounded-lg border border-border space-y-6">
          <h2 className="text-lg font-semibold text-foreground">Imagens</h2>
          <Controller
            name="images"
            control={control}
            render={({ field }) => (
              <ImageList
                images={field.value}
                onChange={field.onChange}
                error={getErrorMessage(errors.images)}
              />
            )}
          />
        </section>

        {/* Especificações Técnicas */}
        <section className="bg-card p-6 rounded-lg border border-border space-y-6">
          <h2 className="text-lg font-semibold text-foreground">Especificações Técnicas</h2>
          <SpecificationsList
            specifications={specifications.map((spec) => ({
              ...spec,
              label: ensureLocalizedValue(spec.label),
              value: ensureLocalizedValue(spec.value),
            }))}
            onChange={setSpecifications}
            lang={activeLang}
          />
        </section>

        {/* Características */}
        <section className="bg-card p-6 rounded-lg border border-border space-y-6">
          <h2 className="text-lg font-semibold text-foreground">Características</h2>
          <FeaturesList features={features} onChange={setFeatures} lang={activeLang} />
        </section>

        {/* Informações Adicionais */}
        <section className="bg-card p-6 rounded-lg border border-border space-y-6">
          <h2 className="text-lg font-semibold text-foreground">Informações Adicionais</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Rating */}
            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground block">Avaliação</span>
              <Controller
                name="rating"
                control={control}
                render={({ field }) => (
                  <StarRating value={field.value || 0} onChange={field.onChange} />
                )}
              />
            </div>

            {/* Video URL */}
            <div className="space-y-2">
              <span className="text-sm font-medium text-foreground block">
                URL do Vídeo (Opcional)
              </span>
              <Controller
                name="video"
                control={control}
                render={({ field }) => (
                  <Input {...field} type="url" placeholder="https://youtube.com/..." />
                )}
              />
            </div>
          </div>

          {/* Condição */}
          <Controller
            name="condition"
            control={control}
            render={({ field }) => (
              <LocalizedInput
                label="Condição (Opcional)"
                value={ensureLocalizedValue(field.value)}
                onChange={field.onChange}
                lang={activeLang}
                placeholder={{
                  'pt-BR': 'Ex: Novo, Semi-novo, Usado',
                  'en-US': 'Ex: New, Like New, Used',
                  'es-ES': 'Ex: Nuevo, Semi-nuevo, Usado',
                }}
              />
            )}
          />

          {/* Garantia */}
          <Controller
            name="warranty"
            control={control}
            render={({ field }) => (
              <LocalizedInput
                label="Garantia (Opcional)"
                value={ensureLocalizedValue(field.value)}
                onChange={field.onChange}
                lang={activeLang}
                placeholder={{
                  'pt-BR': 'Ex: 12 meses de garantia',
                  'en-US': 'Ex: 12 months warranty',
                  'es-ES': 'Ex: 12 meses de garantía',
                }}
              />
            )}
          />

          {/* Entrega */}
          <Controller
            name="shipping"
            control={control}
            render={({ field }) => (
              <LocalizedInput
                label="Informações de Entrega (Opcional)"
                value={ensureLocalizedValue(field.value)}
                onChange={field.onChange}
                lang={activeLang}
                placeholder={{
                  'pt-BR': 'Ex: Frete grátis para todo o Brasil',
                  'en-US': 'Ex: Free shipping nationwide',
                  'es-ES': 'Ex: Envío gratis a todo el país',
                }}
              />
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Em Estoque */}
            <div className="flex items-center gap-3">
              <Controller
                name="inStock"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-foreground">Em Estoque</span>
                  </label>
                )}
              />
            </div>

            {/* Destaque */}
            <div className="flex items-center gap-3">
              <Controller
                name="featured"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-foreground">Produto em Destaque</span>
                  </label>
                )}
              />
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <span className="text-sm font-medium text-foreground block">Status</span>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
              )}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-6 border-t border-border">
            {Object.keys(errors).length > 0 && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
                <p className="text-sm text-destructive font-medium">
                  Por favor, corrija os erros no formulário antes de salvar:
                </p>
                <ul className="list-disc list-inside text-sm text-destructive/80 mt-2 space-y-1">
                  {errors.name && <li>Nome do produto (pt-BR) é obrigatório</li>}
                  {errors.description && <li>Descrição (pt-BR) é obrigatória</li>}
                  {errors.images && (
                    <li>
                      {getErrorMessage(errors.images) || 'Adicione pelo menos uma imagem válida'}
                    </li>
                  )}
                  {errors.price && <li>{getErrorMessage(errors.price) || 'Preço inválido'}</li>}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-3">
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                  <CloseCircle size={18} className="mr-2" />
                  Cancelar
                </Button>
              )}
              <Button type="submit" disabled={isLoading}>
                <Diskette size={18} className="mr-2" />
                {isLoading ? 'Salvando...' : 'Salvar Produto'}
              </Button>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}
